import { ProjectWithStats } from '../atoms/v3/projectV2Atoms';
import { Folder } from '../types/folder';

/**
 * 按文件夹分组的项目数据结构
 */
export interface ProjectGroup {
  folder: Folder | null; // null 表示"未分组"
  projects: ProjectWithStats[];
  incompleteCount: number;
  completedCount: number;
}

/**
 * 按文件夹分组项目
 * @param projects 项目列表
 * @param folders 文件夹列表
 * @returns 按文件夹分组的项目数据
 */
export const groupProjectsByFolder = (
  projects: ProjectWithStats[],
  folders: Folder[]
): ProjectGroup[] => {
  // 创建文件夹映射
  const folderMap = new Map<string, Folder>();
  folders.forEach(folder => {
    folderMap.set(folder.id, folder);
  });

  // 按文件夹ID分组项目
  const groupMap = new Map<string | null, ProjectWithStats[]>();
  
  projects.forEach(project => {
    const folderId = project.folderId || null;
    if (!groupMap.has(folderId)) {
      groupMap.set(folderId, []);
    }
    groupMap.get(folderId)!.push(project);
  });

  // 转换为 ProjectGroup 数组
  const groups: ProjectGroup[] = [];

  // 为所有文件夹创建分组（包括空文件夹）
  const folderGroups = folders
    .map(folder => {
      const projects = groupMap.get(folder.id) || []; // 如果没有项目，使用空数组

      return {
        folder,
        projects,
        incompleteCount: projects.filter(p => p.stats.completionRate < 100).length,
        completedCount: projects.filter(p => p.stats.completionRate === 100).length,
      };
    })
    .sort((a, b) => {
      // 按文件夹创建时间排序
      return new Date(a.folder!.createdAt).getTime() - new Date(b.folder!.createdAt).getTime();
    });

  groups.push(...folderGroups);

  // 最后添加未分组的项目
  const ungroupedProjects = groupMap.get(null) || [];
  if (ungroupedProjects.length > 0) {
    groups.push({
      folder: null,
      projects: ungroupedProjects,
      incompleteCount: ungroupedProjects.filter(p => p.stats.completionRate < 100).length,
      completedCount: ungroupedProjects.filter(p => p.stats.completionRate === 100).length,
    });
  }

  return groups;
};

/**
 * 过滤项目分组（根据完成状态）
 * @param groups 项目分组
 * @param showCompleted 是否显示已完成项目
 * @returns 过滤后的项目分组
 */
export const filterProjectGroups = (
  groups: ProjectGroup[],
  showCompleted: boolean
): ProjectGroup[] => {
  return groups.map(group => ({
    ...group,
    projects: showCompleted
      ? group.projects
      : group.projects.filter(p => p.stats.completionRate < 100)
  })).filter(group => group.projects.length > 0);
};

/**
 * 获取文件夹的项目统计
 * @param projects 项目列表
 * @param folderId 文件夹ID（null表示未分组）
 * @returns 项目统计信息
 */
export const getFolderProjectStats = (
  projects: ProjectWithStats[],
  folderId: string | null
) => {
  const folderProjects = projects.filter(p => p.folderId === folderId);
  return {
    total: folderProjects.length,
    incomplete: folderProjects.filter(p => p.stats.completionRate < 100).length,
    completed: folderProjects.filter(p => p.stats.completionRate === 100).length,
  };
};

/**
 * 检查文件夹是否应该显示
 * @param group 项目分组
 * @param showCompleted 是否显示已完成项目
 * @returns 是否应该显示该文件夹
 */
export const shouldShowFolderGroup = (
  group: ProjectGroup,
  showCompleted: boolean
): boolean => {
  // 如果是真实文件夹（非未分组），即使为空也要显示
  if (group.folder !== null) {
    return true;
  }

  // 对于未分组，只有在有项目时才显示
  if (showCompleted) {
    return group.projects.length > 0;
  }
  return group.incompleteCount > 0;
};
